/**
 * Equipment mention provider — registers with the mentions registry so that
 * typing `@` in todo inputs autocompletes against household appliances and
 * chips in display mode open the equipment detail page.
 */

import React from 'react';
import { registerMentionProvider } from '@/features/mentions/registry';
import { MentionChip } from '@/features/mentions/components/MentionChip';
import { getAppliances } from '@/infra/api/client';
import { navigate } from '@/infra/navigation/history';
import { store } from '@/infra/store';
import { selectLoadedAppliances } from './selectors';
import { Appliance } from '@/infra/types/domain';

/** Rank appliances whose name contains the (lower-cased) needle. */
function matchAppliances(appliances: Appliance[], needle: string): Appliance[] {
  return appliances.filter((a) => a.name.toLowerCase().includes(needle));
}

registerMentionProvider({
  kind: 'equipment',
  trigger: '@',
  async search(q) {
    const query = q.trim();
    if (!query) return [];
    const needle = query.toLowerCase();

    // Prefer the already-loaded Redux list so the dropdown works offline and
    // without a round-trip. maintenance is a plugin-loaded reducer, so the
    // store's static state type omits it — cast and let the selector read
    // `state.maintenance`.
    const state = store.getState() as any;
    let appliances = selectLoadedAppliances(state);

    // Fall back to the API only when nothing is loaded globally (e.g. the user
    // never visited the Maintenance page this session).
    if (appliances.length === 0) {
      try {
        appliances = await getAppliances();
      } catch {
        appliances = [];
      }
    }

    return matchAppliances(appliances, needle)
      .slice(0, 8)
      .map((a) => ({
        id: String(a.id),
        label: a.name,
        hint: a.location || a.category || undefined,
      }));
  },
  renderChip({ id, label }) {
    return <MentionChip id={id} label={label} kind="equipment" />;
  },
  onChipClick(id) {
    navigate(`/maintenance/equipment/${id}`);
  },
});
